import { useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import PageHeader from '@/components/PageHeader';
import emptySelectOption from '@/constants/emptySelectOption';
import DrugScreenForm from '@/forms/DrugScreenForm';
import { useAppointmentAttendances } from '@/hooks/useAppointments';
import PersonContext from '@/pages/Person/PersonContext';
import Alert from '@/ui/components/Alert';
import AppointmentAttendanceCard from '@/ui/components/AppointmentAttendanceCard';
import AutocompleteSingleSelect from '@/ui/components/AutocompleteSingleSelect';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import InputGroup from '@/ui/components/Form/InputGroup';
import { OptionType } from '@/ui/components/Form/Select/types';
import Spinner from '@/ui/components/Spinner';
import formatPersonName from '@/utils/formatPersonName';
import usFormatDateString from '@/utils/usFormatDateString';

const PersonNewDrugScreen = () => {
  const [queryParams] = useSearchParams();
  const initialAppointmentId = queryParams.get('appointment-id');
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(
    initialAppointmentId
  );

  const { person } = useContext(PersonContext);
  const [now] = useState(new Date());

  const { data: appointmentsData, isPending: isLoadingAppointments } = useAppointmentAttendances({
    person_id: person.id,
    appointment__start_at__lte: now,
    size: 100,
    order_by: '-appointment__start_at'
  });

  const appointmentOptions = useMemo(() => {
    if (!appointmentsData?.data?.results) return [];

    return appointmentsData.data.results.map((attendance) => {
      const date = attendance.appointment.start_at
        ? usFormatDateString(attendance.appointment.start_at)
        : 'Unknown Date';
      const title = attendance.appointment.title || 'Untitled Appointment';
      return {
        label: `${date} - ${title}`,
        value: attendance?.appointment?.id || ''
      };
    });
  }, [appointmentsData]);

  const selectedAttendance = useMemo(
    () => appointmentsData?.data?.results?.find((a) => a.appointment?.id === selectedAppointmentId),
    [appointmentsData, selectedAppointmentId]
  );

  const handleAppointmentChange = (option: OptionType<string> | null) => {
    setSelectedAppointmentId(option?.value || null);
  };

  return (
    <Container type="page">
      <PageHeader headingText="Record Drug Screen Results" />

      {!initialAppointmentId && (
        <Card className="mb-6">
          <InputGroup>
            {isLoadingAppointments ? (
              <Spinner label="Loading appointments..." />
            ) : appointmentOptions.length === 0 ? (
              <Alert level="info">
                No available appointments for{' '}
                {formatPersonName({ person, withNickname: false, withPronouns: false })}. You must
                first create an appointment to conduct this type of drug screen.
              </Alert>
            ) : (
              <AutocompleteSingleSelect
                label="Select Appointment"
                options={appointmentOptions}
                placeholder="Search for an appointment..."
                onChange={handleAppointmentChange}
                selectedOption={
                  appointmentOptions.find((opt) => opt.value === selectedAppointmentId) ??
                  emptySelectOption
                }
              />
            )}
          </InputGroup>
        </Card>
      )}

      {!initialAppointmentId && selectedAttendance && person.time_zone && (
        <div className="mb-6">
          <AppointmentAttendanceCard
            attendance={selectedAttendance}
            personTimeZone={person.time_zone}
          />
        </div>
      )}

      {selectedAppointmentId && <DrugScreenForm appointmentId={selectedAppointmentId} />}
    </Container>
  );
};

export default PersonNewDrugScreen;
